import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Info, Briefcase, TrendingUp, DollarSign,
  Users, Phone, X, Menu, ChevronRight, Code2,
  Smartphone, Globe, Cpu, Award, BookOpen,
  Shield, FileText, Cookie, MapPin, Monitor
} from 'lucide-react';

const ALL_PAGES = [
  // Core
  { to: '/',                       label: 'Home',                icon: Home,       color: 'text-blue-600',    bg: 'bg-blue-50' },
  { to: '/about',                  label: 'About Us',            icon: Info,       color: 'text-indigo-600',  bg: 'bg-indigo-50' },
  { to: '/leadership',             label: 'Leadership',          icon: Award,      color: 'text-violet-600',  bg: 'bg-violet-50' },
  { to: '/contact',                label: 'Contact',             icon: Phone,      color: 'text-red-600',     bg: 'bg-red-50' },
  { to: '/pricing',                label: 'Pricing',             icon: DollarSign, color: 'text-amber-600',   bg: 'bg-amber-50' },
  { to: '/careers',                label: 'Careers',             icon: Users,      color: 'text-green-600',   bg: 'bg-green-50' },
  // Services
  { to: '/services',               label: 'All Services',        icon: Briefcase,  color: 'text-violet-600',  bg: 'bg-violet-50' },
  { to: '/mobile-app-development', label: 'Mobile App Dev',      icon: Smartphone, color: 'text-blue-600',    bg: 'bg-blue-50' },
  { to: '/ecommerce-development',  label: 'E-commerce Dev',      icon: Globe,      color: 'text-cyan-600',    bg: 'bg-cyan-50' },
  { to: '/enterprise-solutions',   label: 'ERP & CRM',           icon: Code2,      color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { to: '/digital-marketing',      label: 'Digital Marketing',   icon: TrendingUp, color: 'text-pink-600',    bg: 'bg-pink-50' },
  { to: '/attendancesystem',       label: 'Attendance System',   icon: Monitor,    color: 'text-orange-600',  bg: 'bg-orange-50' },
  // Locations
  { to: '/noida',                  label: 'Noida',               icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  { to: '/gorakhpur',              label: 'Gorakhpur',           icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  { to: '/delhi',                  label: 'Delhi',               icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  { to: '/lucknow',                label: 'Lucknow',             icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  { to: '/bangalore',              label: 'Bangalore',           icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  { to: '/mumbai',                 label: 'Mumbai',              icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  { to: '/hyderabad',              label: 'Hyderabad',           icon: MapPin,     color: 'text-gray-600',    bg: 'bg-gray-100' },
  // Content
  { to: '/blog',                   label: 'Blog',                icon: BookOpen,   color: 'text-teal-600',    bg: 'bg-teal-50' },
  // Legal
  { to: '/privacy',                label: 'Privacy Policy',      icon: Shield,     color: 'text-gray-500',    bg: 'bg-gray-100' },
  { to: '/terms',                  label: 'Terms of Service',    icon: FileText,   color: 'text-gray-500',    bg: 'bg-gray-100' },
  { to: '/cookies',                label: 'Cookie Policy',       icon: Cookie,     color: 'text-gray-500',    bg: 'bg-gray-100' },
];

export default function FloatingNav() {
  const [open, setOpen]         = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const location = useLocation();

  // Auto-open after 3 seconds — only once per page load
  useEffect(() => {
    if (autoOpened) return;
    const t = setTimeout(() => {
      setOpen(true);
      setAutoOpened(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [autoOpened]);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="fixed bottom-6 right-5 z-[9999] flex flex-col items-end gap-3 select-none">
      {/* ── Panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1,   y: 0 }}
            exit  ={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
            className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden w-[280px]"
            style={{ transformOrigin: 'bottom right' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">Quick Nav</div>
                <div className="text-sm font-bold text-gray-900">Go to any page</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Links */}
            <div className="p-3 max-h-[60vh] overflow-y-auto">
              {ALL_PAGES.map(({ to, label, icon: Icon, color, bg }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl mb-1 last:mb-0 group transition-all duration-200 ${
                      active
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      active ? 'bg-white/20' : bg
                    }`}>
                      <Icon className={`w-4 h-4 ${active ? 'text-white' : color}`} />
                    </div>
                    <span className={`text-sm font-medium flex-1 ${active ? 'text-white' : 'text-gray-700'}`}>
                      {label}
                    </span>
                    <ChevronRight className={`w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity ${active ? 'text-white opacity-100' : 'text-gray-400'}`} />
                  </Link>
                );
              })}
            </div>

            {/* Footer CTA */}
            <div className="px-3 pb-3">
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="block w-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold py-3 rounded-2xl text-center transition-colors shadow-lg shadow-blue-600/20"
              >
                Get a Free Quote →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Toggle button ── */}
      <motion.button
        onClick={() => setOpen(o => !o)}
        whileTap={{ scale: 0.94 }}
        className={`w-14 h-14 rounded-2xl shadow-xl flex items-center justify-center transition-all duration-300 ${
          open
            ? 'bg-gray-900 text-white'
            : 'bg-blue-600 text-white shadow-blue-600/40'
        }`}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="x"
              initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}>
              <X className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div key="menu"
              initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}>
              <Menu className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
